import type {
  CreatePbxLocationRequest,
  UpdatePbxLocationRequest,
} from '@mangovoice/types'

import { PBX_LOCATIONS_ENDPOINTS } from './endpoints'
import type {
  GetAvailablePhoneNumbersParams,
  GetPbxLocationsParams,
  PbxLocationsApi,
  PbxLocationsApiClient,
} from './pbxLocationsApi.types'

const cleanParams = <T extends object>(
  params: T | undefined,
): Record<string, unknown> | undefined => {
  if (!params) return undefined
  const cleaned = Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value
      }
      return acc
    },
    {} as Record<string, unknown>,
  )
  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

export const createPbxLocationsApi = (
  client: PbxLocationsApiClient,
): PbxLocationsApi => ({
  getPbxLocations: (params: GetPbxLocationsParams) => {
    return client.get(
      PBX_LOCATIONS_ENDPOINTS.LIST,
      cleanParams({
        pbx_id: params.pbxId,
        search: params.search,
        ordering: params.ordering,
        limit: params.limit,
        offset: params.offset,
      }),
    )
  },

  getPbxLocation: (id: number) => {
    return client.get(PBX_LOCATIONS_ENDPOINTS.DETAIL(id))
  },

  createPbxLocation: (body: CreatePbxLocationRequest) => {
    return client.post(PBX_LOCATIONS_ENDPOINTS.LIST, body)
  },

  updatePbxLocation: (id: number, body: UpdatePbxLocationRequest) => {
    return client.patch(PBX_LOCATIONS_ENDPOINTS.DETAIL(id), body)
  },

  deletePbxLocation: (id: number, newLocationId?: number) => {
    return client.delete(
      PBX_LOCATIONS_ENDPOINTS.DETAIL(id),
      cleanParams({ new_location_id: newLocationId }),
    )
  },

  getAvailablePhoneNumbersForLocation: (
    params: GetAvailablePhoneNumbersParams,
  ) => {
    return client.get(
      PBX_LOCATIONS_ENDPOINTS.AVAILABLE_PHONE_NUMBERS,
      cleanParams({
        pbx_id: params.pbxId,
        exclude_location_id: params.excludeLocationId,
      }),
    )
  },
})
